import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, ClockIcon, Loader2Icon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { business, projectTypeOptions, timelineOptions } from '../data/business';
import { serviceAreas } from '../data/serviceAreas';
import { ui } from '../data/ui';
import { fill } from '../utils/format';
import { useEstimateForm, type EstimateField, type RequiredField } from '../hooks/useEstimateForm';
const inputBase = 'mt-1.5 block w-full rounded-lg border bg-white px-4 text-[15px] text-ink placeholder:text-muted transition-[border-color,box-shadow] duration-150 focus:outline-none focus:ring-2 focus:ring-navy/30';
export function ContactSection() {
  const {
    t,
    lang
  } = useLanguage();
  const {
    values,
    errors,
    status,
    update,
    handleSubmit,
    reset
  } = useEstimateForm(lang);
  const c = ui.contact;
  const hasError = (f: EstimateField) => !!errors[f as RequiredField];
  const fieldClass = (f: EstimateField) => `${inputBase} ${hasError(f) ? 'border-danger focus:border-danger' : 'border-line focus:border-navy'}`;
  const aria = (f: EstimateField) => ({
    'aria-invalid': hasError(f),
    'aria-describedby': hasError(f) ? `estimate-${f}-error` : undefined
  });
  const errorText = (f: RequiredField) => errors[f] ? t(c.errors[f]) : undefined;
  return <section id="contact" className="bg-navy py-20 md:py-24" aria-labelledby="contact-title" data-id="element-34" data-mp="qjpq1b7unkil">
      <div className="container-page grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-35" data-mp="lkwdbl0dzxjh">
        <div className="lg:col-span-7" data-id="element-36" data-mp="10bbw6t5byteh">
          <h2 id="contact-title" className="font-display text-[28px] font-bold leading-tight text-white md:text-4xl" data-id="element-37" data-mp="10aai0ft0pqsj">
            {t(c.title)}
          </h2>
          <p className="mt-4 text-lg text-navy-soft" data-id="element-38" data-mp="19az0bmpg2db8">{t(c.subtitle)}</p>

          <div className="mt-8 rounded-2xl bg-white p-6 shadow-xl md:p-8" data-id="element-39" data-mp="14cfcthv7ctgr">
            <AnimatePresence mode="wait" initial={false} data-id="element-40" data-mp="14e8hwsq635mn">
              {status === 'success' ? <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.96
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} className="py-10 text-center" role="status" data-id="element-41" data-mp="1wwlrirq85ps">
                  <CheckCircle2Icon className="mx-auto h-14 w-14 text-leaf-strong" aria-hidden="true" data-id="element-42" data-mp="koz5j2zo0249" />
                  <h3 className="mt-4 font-display text-2xl font-bold text-ink" data-id="element-43" data-mp="pmqxfx0b7o8g">{fill(t(c.successTitle), {
                  name: values.name.split(' ')[0]
                })}</h3>
                  <p className="mx-auto mt-2 max-w-md text-body" data-id="element-44" data-mp="yp0aatj1vtp0">{fill(t(c.successBody), {
                  phone: values.phone
                })}</p>
                  <button type="button" onClick={reset} className="mt-6 font-semibold text-navy underline underline-offset-4" data-id="element-45" data-mp="2wftg49gvwnwa">
                    {t(c.another)}
                  </button>
                </motion.div> : <motion.form key="form" noValidate onSubmit={handleSubmit} initial={false} exit={{
              opacity: 0
            }} transition={{
              duration: 0.15
            }} aria-label={t(c.formLabel)} data-id="element-46" data-mp="2syf63f6r0sid">
                  <div className="grid gap-5 sm:grid-cols-2" data-id="element-47" data-mp="2j840lo7f2b9d">
                    <Field id="name" label={t(c.name)} error={errorText('name')} required data-id="element-48" data-mp="2f5za2yc9luqy">
                      <input id="estimate-name" type="text" autoComplete="name" value={values.name} onChange={e => update('name', e.target.value)} className={`${fieldClass('name')} h-12`} placeholder={t(c.namePh)} {...aria('name')} data-id="element-49" data-mp="3bvzxaswzk365" />
                    </Field>
                    <Field id="phone" label={t(c.phone)} error={errorText('phone')} required data-id="element-50" data-mp="32tgto6ldlmiu">
                      <input id="estimate-phone" type="tel" autoComplete="tel" value={values.phone} onChange={e => update('phone', e.target.value)} className={`${fieldClass('phone')} h-12`} placeholder="(614) 555-0123" {...aria('phone')} data-id="element-51" data-mp="2xvp59mjxwije" />
                    </Field>
                    <Field id="email" label={t(c.email)} error={errorText('email')} required className="sm:col-span-2" data-id="element-52" data-mp="3adhsvm4338jx">
                      <input id="estimate-email" type="email" autoComplete="email" value={values.email} onChange={e => update('email', e.target.value)} className={`${fieldClass('email')} h-12`} placeholder="jane@company.com" {...aria('email')} data-id="element-53" data-mp="3f612y93kbljz" />
                    </Field>
                    <Field id="projectType" label={t(c.projectType)} error={errorText('projectType')} required data-id="element-54" data-mp="3t3zgjca27dyf">
                      <select id="estimate-projectType" value={values.projectType} onChange={e => update('projectType', e.target.value)} className={`${fieldClass('projectType')} h-12`} {...aria('projectType')} data-id="element-55" data-mp="1wmx0qlpeofc">
                        <option value="" data-id="element-56" data-mp="3p12sv75nwrnm">{t(c.select)}</option>
                        {projectTypeOptions.map(o => <option key={o.value} value={o.value} data-id="element-57" data-mp="28qgux3bkj97a">
                            {t(o.label)}
                          </option>)}
                      </select>
                    </Field>
                    <Field id="location" label={t(c.location)} error={errorText('location')} required data-id="element-58" data-mp="2154zf15161is">
                      <select id="estimate-location" value={values.location} onChange={e => update('location', e.target.value)} className={`${fieldClass('location')} h-12`} {...aria('location')} data-id="element-59" data-mp="2f4e7fdj31pbo">
                        <option value="" data-id="element-60" data-mp="1mfp91m9evy3m">{t(c.selectCity)}</option>
                        {serviceAreas.map(a => <option key={a.city} value={a.city} data-id="element-61" data-mp="1hhnk4kpb14k8">
                            {a.city}, OH
                          </option>)}
                        <option value="other" data-id="element-62" data-mp="1rf1zsi3elpbh">{t(c.otherCity)}</option>
                      </select>
                    </Field>
                    <Field id="timeline" label={t(c.timeline)} data-id="element-63" data-mp="13opfe7qz2040">
                      <select id="estimate-timeline" value={values.timeline} onChange={e => update('timeline', e.target.value)} className={`${fieldClass('timeline')} h-12`} data-id="element-64" data-mp="yqe741gbrxnw">
                        <option value="" data-id="element-65" data-mp="2ysdtg85ou54b">{t(c.select)}</option>
                        {timelineOptions.map(o => <option key={o.value} value={o.value} data-id="element-66" data-mp="33pdzwk6g99vy">
                            {t(o.label)}
                          </option>)}
                      </select>
                    </Field>
                    <fieldset data-id="element-67" data-mp="2fub7614iyma7">
                      <legend className="text-sm font-semibold text-ink" data-id="element-68" data-mp="2fv31z9qasfzf">{t(c.language)}</legend>
                      <div className="mt-1.5 grid h-12 grid-cols-2 gap-1 rounded-lg border border-line p-1" data-id="element-69" data-mp="2ktnuso37zxdu">
                        {(['en', 'es'] as const).map(l => <label key={l} lang={l} className={`flex cursor-pointer items-center justify-center rounded-md text-sm font-semibold transition-colors duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-navy ${values.language === l ? 'bg-navy text-white' : 'text-body hover:bg-mist'}`} data-id="element-70" data-mp="2tu2j7wbhdslk">
                            <input type="radio" name="language" value={l} checked={values.language === l} onChange={() => update('language', l)} className="sr-only" data-id="element-71" data-mp="9h7bqo3b02xm" />
                            {l === 'en' ? 'English' : 'Español'}
                          </label>)}
                      </div>
                    </fieldset>
                    <Field id="details" label={t(c.details)} className="sm:col-span-2" data-id="element-72" data-mp="3rlh9p6qefnfd">
                      <textarea id="estimate-details" rows={4} value={values.details} onChange={e => update('details', e.target.value)} className={`${fieldClass('details')} py-3`} placeholder={t(c.detailsPh)} data-id="element-73" data-mp="3moqt8iaomptf" />
                    </Field>
                  </div>

                  {status === 'error' && <p role="alert" className="mt-5 flex items-start gap-2 rounded-lg bg-danger/10 p-3 text-sm text-danger" data-id="element-74" data-mp="1gvirp21qaspx">
                      <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-75" data-mp="2064tshqbt1s3" />
                      {fill(t(c.errorMsg), {
                  phone: business.phoneDisplay
                })}
                    </p>}

                  <button type="submit" disabled={status === 'submitting'} className="btn btn-green mt-6 w-full disabled:cursor-not-allowed disabled:opacity-80" data-id="element-76" data-mp="1vd26usg20qkv">
                    {status === 'submitting' ? <>
                        <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-77" data-mp="1lfntj1pfghws" />
                        {t(c.sending)}
                      </> : t(ui.requestEstimate)}
                  </button>
                  <p className="mt-3 text-center text-xs text-muted" data-id="element-78" data-mp="31sl1s2j4m132">{t(c.noObligation)}</p>
                </motion.form>}
            </AnimatePresence>
          </div>
        </div>

        <aside className="lg:col-span-5 lg:pt-[120px]" aria-label={t(c.infoLabel)} data-id="element-79" data-mp="3gvw6ofl622zf">
          <a href={business.phoneHref} className="block rounded-2xl bg-navy-deep p-6 transition-colors duration-150 hover:bg-navy-ink" data-id="element-80" data-mp="3bx1gutos7e5t">
            <span className="text-sm text-navy-soft" data-id="element-81" data-mp="3pphn03czbsay">{t(c.callOrText)}</span>
            <span className="mt-1 flex items-center gap-3 font-display text-2xl font-extrabold text-white md:text-3xl" data-id="element-82" data-mp="3kxzij5bqz9l8">
              <PhoneIcon className="h-6 w-6 text-leaf-light" aria-hidden="true" data-id="element-83" data-mp="3g6mk2m1v23c" />
              {business.phoneDisplay}
            </span>
            <span className="mt-2 block text-sm font-medium text-leaf-light" data-id="element-84" data-mp="3upn78ygx6cj0">{t(c.infoTagline)}</span>
          </a>

          <address className="mt-8 space-y-6 not-italic" data-id="element-85" data-mp="cflbuyqhd6m9">
            <InfoRow icon={<MapPinIcon className="h-5 w-5" data-id="element-87" data-mp="8h42s9ni1m27" />} label={t(c.office)} data-id="element-86" data-mp="8hnlhskz4eha">
              <a href={business.mapsUrl} target="_blank" rel="noopener noreferrer" className="hover:underline" data-id="element-88" data-mp="2eac71xnu7x9e">
                {business.name}
                <br data-id="element-89" data-mp="2a9s7knfskhk3" />
                {business.street}
                <br data-id="element-90" data-mp="yedyk3d9xbf7" />
                {business.city}, {business.region} {business.postalCode}
              </a>
            </InfoRow>
            <InfoRow icon={<ClockIcon className="h-5 w-5" data-id="element-92" data-mp="ue3u4v54qt2g" />} label={t(c.hours)} data-id="element-91" data-mp="udu4j8nzynxa">
              {t(ui.hoursLong)}
            </InfoRow>
            <InfoRow icon={<MailIcon className="h-5 w-5" data-id="element-94" data-mp="ked16xw7wm4h" />} label={t(c.emailLabel)} data-id="element-93" data-mp="kdthpcqx5poc">
              <a href={`mailto:${business.email}`} className="hover:underline" data-id="element-95" data-mp="1i1bz1bync03y">
                {business.email}
              </a>
            </InfoRow>
          </address>

          <div className="mt-8 border-t border-white/15 pt-6" data-id="element-96" data-mp="1d30o0xwm7jk5">
            <p className="text-sm font-semibold text-white" data-id="element-97" data-mp="18ah1vcjv3g3q">{t(c.areas)}</p>
            <p className="mt-2 text-sm leading-relaxed text-navy-soft" data-id="element-98" data-mp="13cxoeeuzn7cu">{serviceAreas.map(a => a.city).join(' · ')}, Ohio</p>
          </div>
        </aside>
      </div>
    </section>;
}
type FieldProps = {
  id: EstimateField;
  label: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
};
function Field({
  id,
  label,
  error,
  required,
  className = '',
  children
}: FieldProps) {
  return <div className={className} data-id="element-99" data-mp="28bul1nc7z3ot">
      <label htmlFor={`estimate-${id}`} className="text-sm font-semibold text-ink" data-id="element-100" data-mp="28d5mvhm3u1da">
        {label}
        {required && <span className="text-danger" aria-hidden="true" data-id="element-101" data-mp="2ia1msyxxtf68">
            {' '}
            *
          </span>}
      </label>
      {children}
      {error && <p id={`estimate-${id}-error`} className="mt-1.5 text-sm text-danger" data-id="element-102" data-mp="2m99er49i9xml">
          {error}
        </p>}
    </div>;
}
function InfoRow({
  icon,
  label,
  children
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return <div className="flex gap-4" data-id="element-103" data-mp="30yoqpe4dnyi8">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-leaf-light" aria-hidden="true" data-id="element-104" data-mp="3122sak57w3om">
        {icon}
      </span>
      <div data-id="element-105" data-mp="3a1ptp5xy59ho">
        <p className="text-sm text-navy-soft" data-id="element-106" data-mp="3a17qtqgs3x0s">{label}</p>
        <div className="mt-0.5 text-white" data-id="element-107" data-mp="1aveqxdhuvzoe">{children}</div>
      </div>
    </div>;
}